<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';

import {
  isPostRemovable,
  postChannelType,
  postCoverMedia,
  postMediaCount,
  postStatusLabelKey,
  postStatusTextColor,
  postTypeLabelKey,
} from 'dashboard/helper/wgptSocialMediaPosts';

import Icon from 'next/icon/Icon.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Button from 'next/button/Button.vue';
import Dialog from 'next/dialog/Dialog.vue';
import Dropdown from 'next/wgpt/Dropdown/Dropdown.vue';
import DropdownMenu from 'next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  post: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { isAdmin } = useAdmin();
const getInbox = useMapGetter('inboxes/getInbox');
const uiFlags = useMapGetter('wgptSocialMediaPosts/getUIFlags');

// getInbox returns {} for a missing inbox - normalize to null so fallbacks fire.
const inbox = computed(() => {
  const record = getInbox.value(props.post.inbox_id);
  return record?.id ? record : null;
});
// When the inbox is gone, keep the channel branding by deriving it from the post type.
const channelIconInbox = computed(
  () => inbox.value || { channel_type: postChannelType(props.post) }
);

const coverMedia = computed(() => postCoverMedia(props.post));
const previewUrl = computed(() => coverMedia.value?.url);
const isVideoPreview = computed(() => Boolean(coverMedia.value?.video));
const coverThumbnailUrl = computed(() => coverMedia.value?.thumbnail_url);
const extraMediaCount = computed(() => postMediaCount(props.post) - 1);

const externalUrl = computed(() => props.post.external_url);
// The platform link is only meaningful while the post is actually live.
const canOpenExternal = computed(
  () => props.post.status === 'published' && Boolean(externalUrl.value)
);
const isRemovable = computed(() => isPostRemovable(props.post));

const statusTextColor = computed(() => postStatusTextColor(props.post.status));
const statusText = computed(() =>
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  t(postStatusLabelKey(props.post.status))
);
const postTypeText = computed(() =>
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  t(postTypeLabelKey(props.post))
);

const menuItems = computed(() => [
  ...(canOpenExternal.value
    ? [
        {
          label: t('WGPT_SOCIAL_MEDIA_POSTS.CONTEXT_MENU.OPEN_POST'),
          value: 'open_external',
          action: 'open_external',
          icon: 'i-lucide-external-link',
        },
      ]
    : []),
  ...(isRemovable.value
    ? [
        {
          label: t('WGPT_SOCIAL_MEDIA_POSTS.REMOVE.BUTTON'),
          value: 'remove',
          action: 'remove',
          icon: 'i-lucide-trash-2',
        },
      ]
    : []),
]);

// 'all' is a sentinel, not a real inbox id - it preserves "opened from the All list" across the
// detail view so its breadcrumb/back action (and the sidebar's highlight) can return to All
// instead of always landing on this post's own channel (see Sidebar.vue's 'All' entry).
const goToDetails = () => {
  const inboxId =
    route.name === 'social_media_posts_overview' ? 'all' : props.post.inbox_id;

  router.push({
    name: 'social_media_posts_show',
    params: { inboxId, postId: props.post.id },
  });
};

const removeDialogRef = ref(null);
const isRemoving = computed(() => uiFlags.value.isRemoving);

const handleRemove = async () => {
  try {
    await store.dispatch('wgptSocialMediaPosts/remove', props.post.id);
    removeDialogRef.value?.close();
    useAlert(t('WGPT_SOCIAL_MEDIA_POSTS.REMOVE.MESSAGES.SUCCESS'));
  } catch (error) {
    useAlert(error.message);
  }
};

const handleAction = ({ action }, toggle) => {
  toggle();
  if (action === 'open_external' && externalUrl.value) {
    window.open(externalUrl.value, '_blank', 'noopener,noreferrer');
  } else if (action === 'remove') {
    removeDialogRef.value?.open();
  }
};
</script>

<template>
  <div
    class="flex flex-col w-full overflow-hidden border border-n-container rounded-xl bg-n-solid-2"
  >
    <div
      role="button"
      tabindex="0"
      class="relative w-full overflow-hidden aspect-square bg-n-slate-3 cursor-pointer"
      @click="goToDetails"
      @keydown.enter="goToDetails"
    >
      <img
        v-if="isVideoPreview && coverThumbnailUrl"
        :src="coverThumbnailUrl"
        class="object-cover w-full h-full"
        loading="lazy"
        alt=""
      />
      <video
        v-else-if="isVideoPreview"
        :src="previewUrl"
        class="object-cover w-full h-full"
        muted
        playsinline
        preload="metadata"
      />
      <img
        v-else-if="previewUrl"
        :src="previewUrl"
        class="object-cover w-full h-full"
        loading="lazy"
        alt=""
      />
      <div
        v-if="isVideoPreview"
        class="absolute inset-0 flex items-center justify-center bg-[rgba(0,0,0,0.2)] pointer-events-none"
      >
        <Icon icon="i-lucide-play" class="text-white size-8 drop-shadow" />
      </div>
      <div
        v-else
        class="flex items-center justify-center w-full h-full bg-gradient-to-br from-n-slate-3 to-n-slate-4"
      >
        <Icon icon="i-lucide-image" class="size-6 text-n-slate-11" />
      </div>
      <span
        class="absolute text-xxs font-medium px-1.5 py-0.5 rounded-md top-1.5 ltr:left-1.5 rtl:right-1.5 bg-[rgba(0,0,0,0.6)] text-white"
      >
        {{ postTypeText }}
      </span>
      <span
        v-if="extraMediaCount > 0"
        class="absolute text-xxs font-medium px-1.5 py-0.5 rounded-md bottom-1.5 ltr:right-1.5 rtl:left-1.5 bg-[rgba(0,0,0,0.6)] text-white"
      >
        {{
          t('WGPT_SOCIAL_MEDIA_POSTS.GRID.MORE_COUNT', {
            count: extraMediaCount,
          })
        }}
      </span>
    </div>

    <div class="flex flex-col gap-2 p-3">
      <p
        class="min-w-0 mb-0 text-sm truncate cursor-pointer text-n-slate-12 hover:underline"
        @click="goToDetails"
      >
        {{ post.caption }}
      </p>

      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center flex-1 min-w-0 gap-1.5">
          <ChannelIcon :inbox="channelIconInbox" class="size-4 shrink-0" />
          <span
            class="text-xs truncate text-n-slate-11"
            :class="{ italic: !inbox }"
          >
            {{ inbox?.name || t('WGPT_SOCIAL_MEDIA_POSTS.DELETED_INBOX') }}
          </span>
        </div>
        <span
          class="text-xs font-medium inline-flex items-center shrink-0 h-6 px-2 py-0.5 rounded-md bg-n-alpha-2"
          :class="statusTextColor"
        >
          {{ statusText }}
        </span>
        <Dropdown
          v-if="isAdmin && menuItems.length"
          align="end"
          class="flex items-center ltr:ml-auto rtl:mr-auto"
        >
          <template #trigger="{ toggle }">
            <Button
              icon="i-lucide-ellipsis-vertical"
              color="slate"
              size="xs"
              class="rounded-md"
              @click="toggle"
            />
          </template>
          <template #default="{ toggle }">
            <DropdownMenu
              class="!static"
              :menu-items="menuItems"
              @action="e => handleAction(e, toggle)"
            />
          </template>
        </Dropdown>
      </div>
    </div>

    <Dialog
      ref="removeDialogRef"
      type="alert"
      :title="t('WGPT_SOCIAL_MEDIA_POSTS.REMOVE.TITLE')"
      :description="t('WGPT_SOCIAL_MEDIA_POSTS.REMOVE.DESCRIPTION')"
      :confirm-button-label="t('WGPT_SOCIAL_MEDIA_POSTS.REMOVE.CONFIRM')"
      :is-loading="isRemoving"
      :disable-confirm-button="isRemoving"
      @confirm="handleRemove"
    />
  </div>
</template>
